// Hitting a file size, rather than picking a quality and hoping.
//
// An encoder takes a quality and answers with a size. This module needs the
// other direction, so it searches: probe a quality, look at the size, halve the
// remaining range, repeat. Six halvings pin a quality down to within one part
// in a hundred, which is finer than anyone can see.
//
// Quality alone is not always enough. A 24 megapixel photo squeezed towards
// 500 kB runs out of quality long before it runs out of pixels, and at that
// point the better trade is fewer pixels rather than worse ones: a half-size
// picture at quality 0.8 beats a full-size one at 0.3 at the same file size,
// and it is not close. So the search spends resolution to buy quality back,
// and the caller is told when it did, because that is a visible change to
// someone's file and it should never happen silently.

import { draw, encode, sizeOf, fitWithin } from './images.js';

// Nothing is gained above this. The encoders spend a lot of bytes on the last
// few percent and the difference does not show.
const MAX_QUALITY = 0.95;

// What the search tries to be able to afford. Once the picture has been made
// smaller, it is made small enough that this quality fits, not merely small
// enough that the bare minimum fits.
const AIM_QUALITY = 0.72;

// The lowest quality worth accepting in exchange for keeping every pixel.
//
// Set low on purpose. Changing someone's dimensions is the more surprising of
// the two outcomes, and there is a control on the page for anyone who wants it,
// so the search holds on to the resolution until the quality would be visibly
// bad rather than merely soft. Around 0.4 is where JPEG starts showing its
// blocks; above that, the pixels are worth more.
const KEEP_QUALITY = 0.4;

// The absolute floor, used only once the picture is already as small as it is
// allowed to get. At that point anything beats missing the target.
const MIN_QUALITY = 0.3;

// Halvings of the quality range. Each one doubles the precision.
const QUALITY_PROBES = 6;

// How often the picture may be made smaller before the attempt is given up.
const MAX_SCALE_STEPS = 6;

// Below this a result is unrecognisable, and reporting a miss is more useful
// than handing back a thumbnail nobody asked for.
const MIN_SCALE = 0.08;

/**
 * The highest quality whose output still fits, on a canvas that is already the
 * right size.
 *
 * `low` and `known` are a quality that fits and its result, so the search
 * always has an answer to fall back on and never returns nothing. `high` is a
 * quality that is known not to fit.
 */
async function searchQuality(canvas, mime, targetBytes, low, high, known) {
    let best = { blob: known, quality: low };
    let lo = low;
    let hi = high;

    for (let probe = 0; probe < QUALITY_PROBES; probe += 1) {
        const quality = (lo + hi) / 2;
        const blob = await encode(canvas, mime, quality);

        if (blob.size <= targetBytes) {
            best = { blob, quality };
            lo = quality;
        } else {
            hi = quality;
        }
    }

    return best;
}

/**
 * Compress one image until it fits into `targetBytes`.
 *
 * @param {ImageBitmap|HTMLImageElement} image
 * @param {object} options
 * @param {number} options.targetBytes  The size to come in under.
 * @param {string} options.mime         Output type, has to be a lossy one.
 * @param {string} [options.background] Colour under transparent pixels, for JPEG.
 * @param {number} [options.maxEdge]    Optional cap on the long edge, applied first.
 * @returns {Promise<{blob: Blob, width: number, height: number, quality: number,
 *                    scale: number, reached: boolean}>}
 *          `reached` is false when even the smallest allowed version misses the
 *          target; the blob is then the smallest this managed to produce.
 */
export async function compressToTarget(image, { targetBytes, mime, background, maxEdge }) {
    const source = sizeOf(image);

    // The cap the visitor asked for is not part of the search. It applies once,
    // up front, and the search works from that size downwards.
    const capped = maxEdge
        ? fitWithin(source.width, source.height, maxEdge, maxEdge)
        : source;

    let scale = 1;
    let closest = null;

    for (let step = 0; step < MAX_SCALE_STEPS; step += 1) {
        const atSmallest = scale <= MIN_SCALE;
        const width = Math.max(1, Math.round(capped.width * scale));
        const height = Math.max(1, Math.round(capped.height * scale));
        const canvas = draw(image, width, height, background);

        const remember = (blob, quality) => {
            if (!closest || blob.size < closest.blob.size) {
                closest = { blob, width, height, quality, scale, reached: false };
            }
        };

        // The best case. If full quality already fits there is nothing to
        // search for, because no higher quality exists to try.
        const top = await encode(canvas, mime, MAX_QUALITY);
        if (top.size <= targetBytes) {
            return { blob: top, width, height, quality: MAX_QUALITY, scale, reached: true };
        }
        remember(top, MAX_QUALITY);

        // The quality worth having. Everything below this is a compromise, so
        // it is also the measurement the next size is estimated from.
        const aim = await encode(canvas, mime, AIM_QUALITY);
        if (aim.size <= targetBytes) {
            const best = await searchQuality(canvas, mime, targetBytes, AIM_QUALITY, MAX_QUALITY, aim);
            return { blob: best.blob, width, height, quality: best.quality, scale, reached: true };
        }
        remember(aim, AIM_QUALITY);

        // Still at the original size: check whether an acceptable quality saves
        // the resolution before giving any of it away.
        if (step === 0) {
            const keep = await encode(canvas, mime, KEEP_QUALITY);
            if (keep.size <= targetBytes) {
                const best = await searchQuality(canvas, mime, targetBytes, KEEP_QUALITY, AIM_QUALITY, keep);
                return { blob: best.blob, width, height, quality: best.quality, scale, reached: true };
            }
            remember(keep, KEEP_QUALITY);
        }

        // As small as it may get, so the quality floor comes off.
        if (atSmallest) {
            const floor = await encode(canvas, mime, MIN_QUALITY);
            if (floor.size <= targetBytes) {
                const best = await searchQuality(canvas, mime, targetBytes, MIN_QUALITY, AIM_QUALITY, floor);
                return { blob: best.blob, width, height, quality: best.quality, scale, reached: true };
            }
            remember(floor, MIN_QUALITY);
            break;
        }

        // File size follows pixel count, so the scale that would have fit is
        // roughly the square root of how far off this one was. A little under
        // that, because the relationship is not exact and overshooting costs a
        // whole extra round. Clamped so a step always makes progress and never
        // skips past the smallest size the search is allowed to try.
        const estimate = scale * Math.sqrt(targetBytes / aim.size) * 0.95;
        scale = Math.max(MIN_SCALE, Math.min(estimate, scale * 0.9));
    }

    return closest;
}
